import { crossEnv } from '@sbvr/lbe-environment';
import ky from 'ky';
import { parseQueryString } from '@sbvr/lbe-helpers/url';
import axios from 'axios';

function getCookieDomain (): string {
  return `.${window.location.hostname.split('.').slice(-2).join('.')}`;
}

function getEnvironmentKey (): { ENV: string; API: string } {
  const { env = crossEnv.ENV, review = crossEnv.REVIEW_PREFIX, api = crossEnv.API_URL } = parseQueryString();
  const key = [env, ...(review ? [review] : [])].join('-');
  return { ENV: key, API: api };
}

const { ENV, API } = getEnvironmentKey();
const TOKEN_KEY = `sbvr_token-${ENV}`;
const TENANT_ID_KEY = `sbvr_tenant_id-${ENV}`;
export const NET_TOKEN_KEY = `net_token-${ENV}`;

const authChangedSubscribers: Set<() => void> = new Set();
function fireAuthChangedCallbacks (): void {
  for (const cb of authChangedSubscribers) {
    cb();
    authChangedSubscribers.delete(cb);
  }
};
export function onAuthChanged (callback: () => void): void {
  authChangedSubscribers.add(callback);
};

window.addEventListener('storage', function (e) {
  const { storageArea, key } = e;
  if (storageArea === localStorage && (key === TOKEN_KEY || key === TENANT_ID_KEY)) {
    fireAuthChangedCallbacks();
  }
});

export async function logout (): Promise<void> {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TENANT_ID_KEY);
  localStorage.removeItem(NET_TOKEN_KEY);
  await ky.post(`https://${API}/v1/auth/logout`, {
    json: {
      env: ENV,
      domain: getCookieDomain(),
    },
    credentials: 'include',
    retry: 3,
    timeout: 5000,
  });
  fireAuthChangedCallbacks();
};

export async function persistIdentity ({ token, tenantId, netToken }: { token: string; tenantId?: string; netToken?: string }): Promise<void> {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TENANT_ID_KEY, tenantId || '');
  localStorage.setItem(NET_TOKEN_KEY, netToken || '');
  await ky.post(`https://${API}/v1/auth/login`, {
    json: {
      token,
      tenantId,
      env: ENV,
      domain: getCookieDomain(),
    },
    credentials: 'include',
    retry: 3,
    timeout: 5000,
  });
  fireAuthChangedCallbacks();
};

export async function retrieveIdentity (): Promise<{token: string; tenantId: string; netToken: string }> {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  const tenantId = localStorage.getItem(TENANT_ID_KEY) || '';
  const netToken = localStorage.getItem(NET_TOKEN_KEY) || '';
  if (token) {
    return {
      token,
      tenantId,
      netToken,
    };
  }
  return await ky(`https://${API}/v1/auth/token?env=${ENV}`, { credentials: 'include', retry: 3, timeout: 5000 }).json();
};

// TODO: A .NET login function exists in the shared resources, but they're not included in this project
export async function netLogin (email, password): Promise<void> {
  try {
    let url = `https://${crossEnv.MAIN_NET_URL}/account/login`;
    if (crossEnv.MAIN_NET_URL.indexOf('kestrel') !== -1) {
      url = url.replace('https', 'http').replace(':7', ':5');
      // On development, with the missing SSL certificate, http requests are blocked because of mixed-content.
      // Enable this in the site settings (lock icon in address bar / Site settings / Insecure content for Chrome).
      // Do not use HttpRedirection in .NET!
    }
    await axios.post(url, {
      email: email,
      password: password,
    }, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      timeout: 2000,
    }).then(async function (response) {
      if (response.data.accessToken) {
        localStorage.setItem(NET_TOKEN_KEY, response.data.accessToken);
      }
    }).catch(function (error) {
      if (error.message !== 'timeout of 2000ms exceeded') {
        throw error;
      }
    });
  } catch (e) {
    // do nothing
  }
}
